import React, { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";

const DailyReportView = ({ appointments = [], selectedDate }) => {
  const { user, profile } = useAuth();
  const [reportData, setReportData] = useState({
    makeFriend: 0,
    calls: 0,
    app: {
      s1and2: 0,
      s3: 0,
      s4: 0,
      p: 0
    },
    prePlan: false,
    bp: false,
    read: false,
    kpis: {
      bp: 0,
      sav: 0,
      pt: 0,
      nl: 0,
      tl: 0,
      bis: 0,
      be: 0
    }
  });

  const [monthData, setMonthData] = useState({
    bp: 0,
    sav: 0,
    pt: 0,
    nl: 0,
    tl: 0,
    bis: 0,
    be: 0
  });

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    // To Do / Did
    makeFriendTodo: 0,
    makeFriendDid: 0,
    callsTodo: 0,
    callsDid: 0,
    appS1and2Todo: 0,
    appS1and2Did: 0,
    appS3Todo: 0,
    appS3Did: 0,
    appS4Todo: 0,
    appS4Did: 0,
    appPTodo: 0,
    appPDid: 0,
    prePlan: false,
    bpFlag: false,
    readFlag: false,

    // Month to Date
    bp: 0,
    sav: 0,
    pt: 0,
    nl: 0,
    tl: 0,
    bis: 0,
    be: 0,

    // Today
    todayBp: 0,
    todaySav: 0,
    todayPt: 0,
    todayNl: 0,
    todayTl: 0,
    todayBis: 0,
    todayBe: 0,

    // Total in company
    companyTl: 0,
    companyBis: 0,

    notes: ""
  });

  // Calculate KPIs from appointments
  useEffect(() => {
    if (appointments && appointments.length > 0) {
      // Initialize counts
      const todayData = {
        makeFriend: 0,
        calls: 0,
        app: { s1and2: 0, s3: 0, s4: 0, p: 0 },
        prePlan: false,
        bp: false,
        read: false,
        kpis: { bp: 0, sav: 0, pt: 0, nl: 0, tl: 0, bis: 0, be: 0 }
      };

      // Filter appointments for today
      const todayAppointments = appointments.filter(apt => {
        const aptDate = new Date(apt.dateTime);
        return (
          aptDate.getDate() === selectedDate.getDate() &&
          aptDate.getMonth() === selectedDate.getMonth() &&
          aptDate.getFullYear() === selectedDate.getFullYear()
        );
      });

      // Count by type
      todayAppointments.forEach(apt => {
        const type = apt.type?.toUpperCase();
        switch (type) {
          case "MAKE FRIEND":
            todayData.makeFriend += 1;
            break;
          case "CALLS":
            todayData.calls += 1;
            break;
          case "BP":
            todayData.kpis.bp += 1;
            break;
          case "SAV":
            todayData.kpis.sav += 1;
            break;
          case "PT":
            todayData.kpis.pt += 1;
            break;
          case "NL":
            todayData.kpis.nl += 1;
            break;
          case "TL":
            todayData.kpis.tl += 1;
            break;
          case "BIS":
            todayData.kpis.bis += 1;
            break;
          case "BE":
            todayData.kpis.be += 1;
            break;
          default:
            break;
        }
      });

      setReportData(todayData);
    }
  }, [appointments, selectedDate]);

  // Calculate month data
  useEffect(() => {
    if (appointments && appointments.length > 0) {
      const currentYear = selectedDate.getFullYear();
      const currentMonth = selectedDate.getMonth();

      const monthKpis = {
        bp: 0,
        sav: 0,
        pt: 0,
        nl: 0,
        tl: 0,
        bis: 0,
        be: 0
      };

      appointments.forEach(apt => {
        const aptDate = new Date(apt.dateTime);
        if (aptDate.getMonth() === currentMonth && aptDate.getFullYear() === currentYear) {
          const type = apt.type?.toUpperCase();
          switch (type) {
            case "BP":
              monthKpis.bp += 1;
              break;
            case "SAV":
              monthKpis.sav += 1;
              break;
            case "PT":
              monthKpis.pt += 1;
              break;
            case "NL":
              monthKpis.nl += 1;
              break;
            case "TL":
              monthKpis.tl += 1;
              break;
            case "BIS":
              monthKpis.bis += 1;
              break;
            case "BE":
              monthKpis.be += 1;
              break;
            default:
              break;
          }
        }
      });

      setMonthData(monthKpis);
    }
  }, [appointments, selectedDate]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="daily-report-view">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h2 className="report-title">My Daily Report</h2>
        </div>
        <div className="header-right">
          <div className="date-display">
            <span className="date-label">Date:</span>
            <span 
              className="date-value update-link"
              onClick={() => setShowModal(true)}
              style={{ cursor: "pointer" }}
            >
              {formatDate(selectedDate)} (📝 Update)
            </span>
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div className="profile-section">
        <div className="profile-left">
          <div className="profile-avatar">
            {profile?.photoURL ? (
              <img src={profile.photoURL} alt={profile?.name || "Profile"} />
            ) : (
              <div className="avatar-placeholder">
                {profile?.name?.charAt(0) || "U"}
              </div>
            )}
          </div>
          <div className="profile-info">
            <h3 className="profile-name">{profile?.name || "User Name"}</h3>
          </div>
        </div>

        {/* KPI Summary */}
        <div className="kpi-summary">
          <div className="kpi-row">
            <div className="kpi-item">
              <div className="kpi-label">Make Friend</div>
              <div className="kpi-count">{reportData.makeFriend}</div>
            </div>
            <div className="kpi-item">
              <div className="kpi-label">Calls</div>
              <div className="kpi-count">{reportData.calls}</div>
            </div>
          </div>

          <div className="kpi-row app-row">
            <div className="kpi-label-full">APP</div>
            <div className="app-counts">
              <div className="app-item">
                <div className="count">{reportData.app.s1and2}</div>
                <div className="label">S 1&2</div>
              </div>
              <div className="app-item">
                <div className="count">{reportData.app.s3}</div>
                <div className="label">S 3</div>
              </div>
              <div className="app-item">
                <div className="count">{reportData.app.s4}</div>
                <div className="label">S 4</div>
              </div>
              <div className="app-item">
                <div className="count">{reportData.app.p}</div>
                <div className="label">P</div>
              </div>
            </div>
          </div>

          <div className="checkboxes-row">
            <label className="checkbox-item">
              <input type="checkbox" checked={reportData.prePlan} readOnly />
              <span>Pre Plan</span>
            </label>
            <label className="checkbox-item">
              <input type="checkbox" checked={reportData.bp} readOnly />
              <span>BP</span>
            </label>
            <label className="checkbox-item">
              <input type="checkbox" checked={reportData.read} readOnly />
              <span>READ</span>
            </label>
          </div>
        </div>
      </div>

      {/* Today Section */}
      <div className="report-section">
        <h3 className="section-title">Today</h3>
        <div className="kpi-grid-report">
          <div className="kpi-grid-item">
            <div className="grid-label">BP</div>
            <div className="grid-value">{reportData.kpis.bp}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">SAV</div>
            <div className="grid-value">{reportData.kpis.sav}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">PT</div>
            <div className="grid-value">{reportData.kpis.pt}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">NL</div>
            <div className="grid-value">{reportData.kpis.nl}</div>
          </div>
          <div className="kpi-grid-divider"></div>
          <div className="kpi-grid-item">
            <div className="grid-label">TL</div>
            <div className="grid-value">{reportData.kpis.tl}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">BIS</div>
            <div className="grid-value">{reportData.kpis.bis}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">BE</div>
            <div className="grid-value">{reportData.kpis.be}</div>
          </div>
        </div>
      </div>

      {/* Month to Date Section */}
      <div className="report-section">
        <h3 className="section-title">Month to Date</h3>
        <div className="kpi-grid-report">
          <div className="kpi-grid-item">
            <div className="grid-label">BP</div>
            <div className="grid-value">{monthData.bp}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">SAV</div>
            <div className="grid-value">{monthData.sav}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">PT</div>
            <div className="grid-value">{monthData.pt}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">NL</div>
            <div className="grid-value">{monthData.nl}</div>
          </div>
          <div className="kpi-grid-divider"></div>
          <div className="kpi-grid-item">
            <div className="grid-label">TL</div>
            <div className="grid-value">{monthData.tl}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">BIS</div>
            <div className="grid-value">{monthData.bis}</div>
          </div>
          <div className="kpi-grid-item">
            <div className="grid-label">BE</div>
            <div className="grid-value">{monthData.be}</div>
          </div>
        </div>
      </div>

      {/* Update Report Modal */}
      {showModal && (
        <div className="modal-overlay-report" onClick={() => setShowModal(false)}>
          <div className="modal-content-report" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-report">
              <h2>Update Daily Report</h2>
              <button 
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body-report">
              {/* To Do / Did */}
              <div className="todo-did-section kpi-grid-centered">
                <h4 className="modal-subtitle">To Do / Did</h4>
                <div className="todo-did-grid">
                  <div className="todo-header">
                    <span className="todo-header-label">Item</span>
                    <span className="todo-header-col">To Do</span>
                    <span className="todo-header-col">Did</span>
                  </div>
                  {[{
                    label: "Make Friend",
                    todoKey: "makeFriendTodo",
                    didKey: "makeFriendDid"
                  },{
                    label: "Calls",
                    todoKey: "callsTodo",
                    didKey: "callsDid"
                  }].map(item => (
                    <div key={item.label} className="todo-row">
                      <div className="todo-label">{item.label}</div>
                      <input
                        type="number"
                        value={formData[item.todoKey]}
                        onChange={(e) => setFormData({...formData, [item.todoKey]: parseInt(e.target.value) || 0})}
                        className="todo-input"
                        placeholder="0"
                        min="0"
                      />
                      <input
                        type="number"
                        value={formData[item.didKey]}
                        onChange={(e) => setFormData({...formData, [item.didKey]: parseInt(e.target.value) || 0})}
                        className="todo-input"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  ))}

                  {/* APP Rows */}
                  {[{
                    label: "APP S 1&2",
                    todoKey: "appS1and2Todo",
                    didKey: "appS1and2Did"
                  },{
                    label: "APP S 3",
                    todoKey: "appS3Todo",
                    didKey: "appS3Did"
                  },{
                    label: "APP S 4",
                    todoKey: "appS4Todo",
                    didKey: "appS4Did"
                  },{
                    label: "APP P",
                    todoKey: "appPTodo",
                    didKey: "appPDid"
                  }].map(item => (
                    <div key={item.label} className="todo-row">
                      <div className="todo-label">{item.label}</div>
                      <input
                        type="number"
                        value={formData[item.todoKey]}
                        onChange={(e) => setFormData({...formData, [item.todoKey]: parseInt(e.target.value) || 0})}
                        className="todo-input"
                        placeholder="0"
                        min="0"
                      />
                      <input
                        type="number"
                        value={formData[item.didKey]}
                        onChange={(e) => setFormData({...formData, [item.didKey]: parseInt(e.target.value) || 0})}
                        className="todo-input"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  ))}

                  <div className="checkbox-row">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={formData.prePlan}
                        onChange={(e) => setFormData({...formData, prePlan: e.target.checked})}
                      />
                      <span>Pre Plan</span>
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={formData.bpFlag}
                        onChange={(e) => setFormData({...formData, bpFlag: e.target.checked})}
                      />
                      <span>BP</span>
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={formData.readFlag}
                        onChange={(e) => setFormData({...formData, readFlag: e.target.checked})}
                      />
                      <span>Read</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Month to Date */}
              <div className="report-kpi-section kpi-grid-centered">
                <h4 className="modal-subtitle">Month to Date</h4>
                <div className="kpi-grid-form">
                  <div className="kpi-form-item">
                    <label>BP</label>
                    <input 
                      type="number" 
                      value={formData.bp}
                      onChange={(e) => setFormData({...formData, bp: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>SAV</label>
                    <input 
                      type="number" 
                      value={formData.sav}
                      onChange={(e) => setFormData({...formData, sav: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>PT</label>
                    <input 
                      type="number" 
                      value={formData.pt}
                      onChange={(e) => setFormData({...formData, pt: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>NL</label>
                    <input 
                      type="number" 
                      value={formData.nl}
                      onChange={(e) => setFormData({...formData, nl: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-grid-divider-form"></div>
                  <div className="kpi-form-item">
                    <label>TL</label>
                    <input 
                      type="number" 
                      value={formData.tl}
                      onChange={(e) => setFormData({...formData, tl: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>BIS</label>
                    <input 
                      type="number" 
                      value={formData.bis}
                      onChange={(e) => setFormData({...formData, bis: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>BE</label>
                    <input 
                      type="number" 
                      value={formData.be}
                      onChange={(e) => setFormData({...formData, be: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Today */}
              <div className="report-kpi-section kpi-grid-centered">
                <h4 className="modal-subtitle">Today</h4>
                <div className="kpi-grid-form">
                  <div className="kpi-form-item">
                    <label>BP</label>
                    <input 
                      type="number" 
                      value={formData.todayBp}
                      onChange={(e) => setFormData({...formData, todayBp: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>SAV</label>
                    <input 
                      type="number" 
                      value={formData.todaySav}
                      onChange={(e) => setFormData({...formData, todaySav: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>PT</label>
                    <input 
                      type="number" 
                      value={formData.todayPt}
                      onChange={(e) => setFormData({...formData, todayPt: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>NL</label>
                    <input 
                      type="number" 
                      value={formData.todayNl}
                      onChange={(e) => setFormData({...formData, todayNl: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-grid-divider-form"></div>
                  <div className="kpi-form-item">
                    <label>TL</label>
                    <input 
                      type="number" 
                      value={formData.todayTl}
                      onChange={(e) => setFormData({...formData, todayTl: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>BIS</label>
                    <input 
                      type="number" 
                      value={formData.todayBis}
                      onChange={(e) => setFormData({...formData, todayBis: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>BE</label>
                    <input 
                      type="number" 
                      value={formData.todayBe}
                      onChange={(e) => setFormData({...formData, todayBe: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Total in your company */}
              <div className="report-kpi-section kpi-grid-centered">
                <h4 className="modal-subtitle">Total in your company</h4>
                <div className="kpi-grid-form kpi-grid-centered">
                  <div className="kpi-form-item">
                    <label>TL</label>
                    <input 
                      type="number" 
                      value={formData.companyTl}
                      onChange={(e) => setFormData({...formData, companyTl: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                  <div className="kpi-form-item">
                    <label>BIS</label>
                    <input 
                      type="number" 
                      value={formData.companyBis}
                      onChange={(e) => setFormData({...formData, companyBis: parseInt(e.target.value) || 0})}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="notes-section-form">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  className="notes-textarea"
                  placeholder="Add any notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>
            </div>

            <div className="modal-footer-report">
              <button 
                className="btn-confirm-send"
                onClick={() => {
                  console.log("Report saved:", formData);
                  setShowModal(false);
                }}
              >
                CONFIRM & SEND ✓
              </button>
              <button 
                className="btn-back"
                onClick={() => setShowModal(false)}
              >
                Back to Calendar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReportView;
